import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import api from 'config/axiosConfig';
import { Action, Issue, Sort, SortField, SortOrder } from 'util/constants';
import { useUser } from 'util/UserContext';
import ActionCardRefactored from './ActionCardRefactored';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import SpillTheTeaModal from 'components/Posts/SpillTheTeaModal';
import ScrollHideContainer from 'ui/ScrollHideContainer';
import { SelectTrigger, SelectValue, SelectItem, SelectContent, Select } from 'ui/InputSelect';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import AddButton from 'ui/AddButton';
import { AxiosError } from 'axios';
import EmptyState from 'ui/EmptyState';
import { apiRoutes } from "util/apiRoutes";

interface Props {
    sort: Sort;
    entity?: Issue;
    entityType?: 'issue';
}

const ActionsSection = ({ sort, entity, entityType }: Props) => {
    const { currentUser, setShowAuthModal } = useUser();
    const [actions, setActions] = useState<Action[]>([]);
    const [error, setError] = useState<string>('');
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [isPrivateFilter, setIsPrivateFilter] = useState<'All' | 'public' | 'private'>('All');
    const [timeFilter, setTimeFilter] = useState<'All' | 'upcoming' | 'past'>('upcoming');
    const [sortField, setSortField] = useState<SortField>('start_at');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [inViewRef, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    useEffect(() => {
        switch (sort) {
            case 'top':
                setSortField('participant_count');
                setSortOrder('desc');
                break;
            case 'new':
                setSortField('created_at');
                setSortOrder('desc');
                break;
            case 'soonest':
                setSortField('start_at');
                setSortOrder('asc');
                break;
            case 'latest':
                setSortField('start_at');
                setSortOrder('desc');
                break;
            default:
                setSortField('start_at');
                setSortOrder('asc');
                break;
        }
    }, [sort]);

    const fetchActions = async () => {
        setIsLoading(true);
        try {
            const params: Record<string, string | number> = { kind: "action" };

            if (entity && entityType) {
                params.issueId = entity.id;
            }

            const response = await api.get<Action[]>(apiRoutes.posts.root, { params });
            setActions(response.data || []);
        } catch (err) {
            if (err instanceof AxiosError && err.response?.status === 404) {
                setError('404: Failed to load actions. Please try again later.');
            } else {
                setError('Failed to load actions. Please try again later.');
            }
            setActions([]); // Ensure actions is always an array even on error
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (inView || (entity && entityType)) {
            fetchActions();
        }
        //eslint-disable-next-line
    }, [entity, entityType, inView]);

    const handleActionAdded = async () => {
        try {
            await fetchActions();
        } catch (err) {
            setError('Failed to refresh actions. Please reload the page.');
        }
    };

    const handleSortChange = (newSort: Sort) => {
        switch (newSort) {
            case 'top':
                setSortField('participant_count');
                setSortOrder('desc');
                break;
            case 'new':
                setSortField('created_at');
                setSortOrder('desc');
                break;
            case 'soonest':
                setSortField('start_at');
                setSortOrder('asc');
                break;
            case 'latest':
                setSortField('start_at');
                setSortOrder('desc');
                break;
        }
    };

    const handleAddActionButtonClick = () => {
        if (!currentUser) {
            setShowAuthModal(true);
        } else {
            setOpenModal(true);
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    // Make sure actions is always an array before filtering
    const filteredActions = (actions || []).filter((action) => {
        // Filter by privacy
        if (isPrivateFilter !== 'All') {
            const isPrivate = action?.properties?.is_private || false;
            if (isPrivateFilter === 'private' && !isPrivate) return false;
            if (isPrivateFilter === 'public' && isPrivate) return false;
        }

        // Filter by time
        if (timeFilter !== 'All') {
            const now = new Date();
            const startDate = action?.properties?.start_at ? new Date(action.properties.start_at) : null;
            
            if (timeFilter === 'upcoming' && startDate && startDate < now) return false;
            if (timeFilter === 'past' && (!startDate || startDate >= now)) return false;
        }

        return true;
    });

    const sortedActions = [...filteredActions].sort((a, b) => {
        if (!sortField) return 0;

        let aValue, bValue;

        switch (sortField) {
            case 'participant_count':
                aValue = a.participant_count || 0;
                bValue = b.participant_count || 0;
                break;
            case 'created_at':
                aValue = new Date(a.created_at || 0).getTime();
                bValue = new Date(b.created_at || 0).getTime();
                break;
            case 'start_at':
                aValue = a.properties?.start_at ? new Date(a.properties.start_at).getTime() : 0;
                bValue = b.properties?.start_at ? new Date(b.properties.start_at).getTime() : 0;
                break;
            default:
                return 0;
        }

        if (sortOrder === 'asc') {
            return aValue - bValue;
        } else {
            return bValue - aValue;
        }
    });

    if (error) {
        return error.match(/404/) ? 
            <NotFoundPage 
                displayStyle="page" 
                errorMessage="Sorry, we couldn't find any actions for this entity." 
                buttonText="View Other Actions" 
                buttonHref="/communitea/actions" 
            /> : 
            <ServerErrorPage />;
    }

    return (
        <div className="actions-section" ref={inViewRef}>
            <ScrollHideContainer 
                className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
                hideOffset={115}
                threshold={114}
            >
                <div className="flex flex-col space-y-1 mb-1">
                    <div className="flex mb-1 mx-1">
                        <AddButton
                            tooltipTitle="Add action"
                            buttonText="Action"
                            className="mx-2 w-full"
                            onClick={handleAddActionButtonClick}
                        />
                    </div>
                    <div className="flex mx-3 gap-1 pb-1">
                        <Select onValueChange={(value: Sort) => handleSortChange(value)}>
                            <SelectTrigger>
                                <SelectValue placeholder="Sort" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="soonest">Soonest</SelectItem>
                                <SelectItem value="latest">Latest</SelectItem>
                                <SelectItem value="top">Most Popular</SelectItem>
                                <SelectItem value="new">Newest Added</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select onValueChange={(value) => setTimeFilter(value as 'All' | 'upcoming' | 'past')}>
                            <SelectTrigger>
                                <SelectValue placeholder="Time" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All">All Actions</SelectItem>
                                <SelectItem value="upcoming">Upcoming</SelectItem>
                                <SelectItem value="past">Past</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select onValueChange={(value) => setIsPrivateFilter(value as 'All' | 'public' | 'private')}>
                            <SelectTrigger>
                                <SelectValue placeholder="Privacy" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All">All</SelectItem>
                                <SelectItem value="public">Public</SelectItem>
                                <SelectItem value="private">Private</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </ScrollHideContainer>
            
            <div className="mt-2">
                {isLoading ? (
                    <LoadingSkeleton count={3} />
                ) : actions.length === 0 ? (
                    <EmptyState
                        title="No actions yet"
                        subtitle={`Be the first to add an action${entity ? ' for this ' + entityType : ''}!`}
                        className="m-4"
                    />
                ) : (
                    <div className="flex flex-col pb-6">
                        {sortedActions.map((action) => (
                            <ActionCardRefactored
                                key={action.id}
                                action={action}
                                truncateDescription={true}
                                isActionProfilePage={false}
                                isEntitySection={true}
                            />
                        ))}
                    </div>
                )}
            </div>
            
            {openModal && (
                <SpillTheTeaModal
                    open={openModal}
                    onClose={handleCloseModal}
                    onActionAdded={handleActionAdded}
                    initialSelectedIssue={entity || null}
                    customTitle="Add action"
                    fixedPostType="action"
                    hidePostTypeButtons={true}
                />
            )}
        </div>
    );
};

export default ActionsSection;
